import { useTranslation } from 'react-i18next';
import { useObjectLimit } from '../hooks/useObjectLimit';
import { cn } from '../utils/utils';
import { AlertTriangle } from 'lucide-react';

/** Where an upgrade is asked for: the address the trial hints name (`limits`, `account.use_cases_trial_limit`). */
const UPGRADE_EMAIL = 'b2b@kolula.com';

/**
 * Banner showing usage against the object limit: the trial's always, a
 * contract's only near its tier.
 *
 * Says WHAT it counts — "1 / 2 Stationen verwendet" or "… Standorte …" — per
 * `useObjectLimit().unit`. It used to say "Objekte" for both, which is the
 * word this admin uses for a third thing entirely (an asset: a compartment, a
 * room). An operator with one station of nine compartments therefore read
 * "1 / 2 Objekte verwendet" and could not tell what it referred to (PO,
 * KGSt-Forum 2026-09-14).
 *
 * When the limit is reached the banner turns to a warning style with an
 * upgrade CTA: a plain-text mailto link to b2b@kolula.com (there is no upgrade
 * page; the paid plan is set up by kolula). A contract tenant at its tier gets
 * "kolula kontaktieren" to the same address instead. Hidden for tenants
 * without a limit.
 *
 * The numbers are the API's (#2202): every create path refuses beyond the
 * limit, and this banner only explains it early.
 */
export default function ObjectLimitBanner() {
    const { t } = useTranslation();
    const { current, max, isAtLimit, isTrial, unit, isContract } = useObjectLimit();

    // A contract tenant (ADR-0001 Amendment 6) sees nothing until its tier is
    // reached, then only the request to contact kolula (PO, 2026-11-02: no
    // warning before). The trial banner is unchanged.
    const showContract = isContract && isAtLimit;
    if (!isTrial && !showContract) return null;

    const usedText = showContract
        ? t('limits.contract_at_limit', { count: max, max })
        : t(`limits.${unit}_used`, { current, max });
    const hintText = showContract ? null : t('limits.at_limit_hint');
    const mailSubject = t(showContract ? 'limits.contract_mail_subject' : 'limits.upgrade_mail_subject');
    const ctaText = t(showContract ? 'limits.contact_kolula' : 'limits.upgrade');

    return (
        <div className={cn(
            'flex items-center gap-3 rounded-lg px-4 py-3 text-sm',
            isAtLimit
                ? 'bg-foreground/5 border border-foreground/20'
                : 'bg-primary/5 border border-primary/10',
        )}
        >
            {/* The circled `current` that used to sit here was removed
                (PO, 2026-09-14): it repeated the first number of the sentence
                beside it, and reading as a badge it invited a click it never
                answered. The warning triangle stays — it marks a state the
                sentence alone does not. */}
            {isAtLimit && (
                <AlertTriangle className={'w-4 h-4 text-foreground shrink-0'} />
            )}

            <div className={'flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 flex-1 min-w-0'}>
                <span className={cn(
                    'font-medium',
                    'text-foreground',
                )}
                >
                    {usedText}
                </span>
                {isAtLimit && hintText && (
                    <span className={'text-foreground/70 text-xs'}>
                        {hintText}
                    </span>
                )}
            </div>

            {isAtLimit && (
                <a
                    className={cn(
                        'px-3 py-1.5',
                        'bg-primary text-text-buttonContrast rounded-md',
                        'text-xs font-medium hover:bg-primary/90 transition-colors shrink-0',
                    )}
                    href={`mailto:${UPGRADE_EMAIL}?subject=${encodeURIComponent(mailSubject)}`}
                >
                    {ctaText}
                </a>
            )}
        </div>
    );
}
